import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Calendar, CalendarModule } from 'primeng/calendar';
import { FRM_FILTERS_UI_V2_PANEL_CLASS, type FiltersUiVersion } from '@zaoblako/web/core/types/filters-ui-version.type';
import moment from 'moment-timezone';

@Component({
  standalone: true,
  selector: 'crm-datepicker',
  templateUrl: './datepicker.component.html',
  styleUrls: ['./datepicker.component.scss'],
  imports: [CommonModule, CalendarModule, FormsModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  host: {
    '[class.frm-filters-ui-v2]': 'filtersUiVersion === "v2"'
  }
})
export class DatepickerComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {
  @ViewChild('calendar', { static: true }) public readonly calendar: Calendar;

  public readonly replaceRegExp = /{[0-9]+}/g;

  public _value: Date | Date[];

  public _minDate: Date;
  public _maxDate: Date;

  @Input()
  public set value(date: Date | string) {
    if (date) {
      this._value = this.parseSingleValue(date);
    } else {
      this._value = null;
    }
  }

  @Input()
  public set valueMultiple(dates: Date[] | string[]) {
    if (dates && Array.isArray(dates)) {
      this._value = dates.map((date) => new Date(date));
    } else {
      this._value = null;
    }
    if (this.selectionMode === 'multiple') {
      this.scheduleApplyMultipleSummaryLabel(this._value as Date[]);
    }
  }

  @Input()
  public set minDate(date: Date | string) {
    if (date) {
      this._minDate = new Date(date);
    } else {
      this._minDate = null;
    }
  }

  @Input()
  public set maxDate(date: Date | string) {
    if (date) {
      this._maxDate = new Date(date);
    }
  }

  @Output() public valueChange = new EventEmitter<Date>();
  @Output() public valueMultipleChange = new EventEmitter<Date[]>();

  @Input() public class: string;
  @Input() public disabled: boolean;
  @Input() public modelOptions: any;
  @Input() public showButtonBar = false;
  @Input() public dateFormat: string;
  @Input() public defaultDate: Date = new Date();
  @Input() public readonlyInput = true;
  @Input() public placeholder: string;
  @Input() public styleClass: string;
  @Input() public inputStyleClass: string;
  @Input() public panelStyleClass: string;
  @Input() public timeOnly = false;
  @Input() public showTime = false;
  @Input() public view: 'date' | 'month' | 'year' = 'date';
  @Input() public selectionMode: 'single' | 'multiple' | 'range' = 'single';
  @Input() public showClear = false;
  @Input() public appendTo: any;
  @Input() public autofocus = false;
  @Input() public stepHour = 1;
  @Input() public stepMinute = 1;
  @Input() public stepSecond = 1;

  @Input() public isNotCheckedExternal = false;

  /** Оформление как у фильтров FRM 2.0 (`_filters-ui-v2.scss`). */
  @Input() public filtersUiVersion: FiltersUiVersion = 'v2';

  /** Сколько дат показывать списком до сводки (как `crm-multi-select`). */
  @Input() public maxSelectedLabels = 2;
  @Input() public selectedItemsLabel = 'Выбрано: {0}';

  public get effectiveMaxSelectedLabels(): number {
    if (this.filtersUiVersion === 'v2') {
      return Math.min(this.maxSelectedLabels ?? 1, 1);
    }
    return this.maxSelectedLabels;
  }

  public get mergedPanelStyleClass(): string {
    const base = this.panelStyleClass?.trim() ?? '';
    const v2 = this.filtersUiVersion === 'v2' ? FRM_FILTERS_UI_V2_PANEL_CLASS : '';
    return [base, v2].filter(Boolean).join(' ');
  }

  private timer: ReturnType<typeof setInterval>;
  private originalUpdateInputfield: (() => void) | null = null;
  private summaryLabelFallbackTimer: ReturnType<typeof setTimeout> | null = null;

  public constructor(private readonly renderer2: Renderer2) {}

  public ngOnInit() {
    this.timer = setInterval(() => {
      if (this.isNotCheckedExternal) {
        (this.calendar?.contentViewChild?.nativeElement as HTMLElement)?.querySelectorAll('span')?.forEach((el) => {
          this.renderer2.addClass(el, 'p-datepicker-not-check-external');
        });

        const monthBtn = (this.calendar?.contentViewChild?.nativeElement as HTMLElement)?.querySelector(
          '.p-datepicker-month'
        );
        if (monthBtn) {
          this.renderer2.addClass(monthBtn, 'p-datepicker-not-check-external');
        }

        const yearBtn = (this.calendar?.contentViewChild?.nativeElement as HTMLElement)?.querySelector(
          '.p-datepicker-year'
        );
        if (yearBtn) {
          this.renderer2.addClass(yearBtn, 'p-datepicker-not-check-external');
        }

        const nextBtn = (this.calendar?.contentViewChild?.nativeElement as HTMLElement)?.querySelector(
          '.p-datepicker-next-icon'
        );
        if (nextBtn) {
          this.renderer2.addClass(nextBtn, 'p-datepicker-not-check-external');
        }

        const prevBtn = (this.calendar?.contentViewChild?.nativeElement as HTMLElement)?.querySelector(
          '.p-datepicker-prev-icon'
        );
        if (prevBtn) {
          this.renderer2.addClass(prevBtn, 'p-datepicker-not-check-external');
        }
      }
    }, 300);
  }

  public ngOnChanges(changes: SimpleChanges): void {
    // Для переключения dateFormat внутри p-calendar
    if (changes.view && !changes.view.firstChange) {
      if (this.selectionMode === 'multiple') {
        this._value = (this._value as Date[]).map((_date) => new Date(_date));
      } else {
        this._value = new Date(this._value as any);
      }
    }

    this.snapValueToLocalMonthStart();

    if (changes.valueMultiple && this.selectionMode === 'multiple') {
      this.scheduleApplyMultipleSummaryLabel(this._value as Date[]);
    }
  }

  public ngAfterViewInit(): void {
    if (this.selectionMode === 'multiple') {
      this.patchMultipleInputfieldDisplay();
      this.scheduleApplyMultipleSummaryLabel();
    }
  }

  public onModelChange(value: Date | Date[]): void {
    if (this.selectionMode === 'multiple') {
      this.valueMultipleChange.emit(value as Date[]);
      this.scheduleApplyMultipleSummaryLabel(value as Date[]);
      return;
    }
    this.valueChange.emit(value as Date);
  }

  private parseSingleValue(date: Date | string): Date {
    if (typeof date === 'string' && /^\d{4}-\d{2}$/.test(date.trim())) {
      return moment(date.trim(), 'YYYY-MM').startOf('month').toDate();
    }

    const parsed = date instanceof Date ? date : new Date(date);
    return this.view === 'month' ? this.toLocalMonthStart(parsed) : parsed;
  }

  private snapValueToLocalMonthStart(): void {
    if (this.view !== 'month' || !this._value) {
      return;
    }

    if (Array.isArray(this._value)) {
      this._value = this._value.map((date) => this.toLocalMonthStart(date));
      return;
    }

    this._value = this.toLocalMonthStart(this._value);
  }

  private toLocalMonthStart(date: Date): Date {
    if (!date || Number.isNaN(date.getTime())) {
      return date;
    }

    const local = moment(date).startOf('month').toDate();
    return local.getTime() === date.getTime() ? date : local;
  }

  private patchMultipleInputfieldDisplay(): void {
    if (!this.calendar || this.originalUpdateInputfield) {
      return;
    }

    this.originalUpdateInputfield = this.calendar.updateInputfield.bind(this.calendar);
    this.calendar.updateInputfield = () => {
      this.originalUpdateInputfield?.();
      this.applyMultipleSummaryLabel();
    };
  }

  private restoreUpdateInputfieldPatch(): void {
    if (this.calendar && this.originalUpdateInputfield) {
      this.calendar.updateInputfield = this.originalUpdateInputfield;
      this.originalUpdateInputfield = null;
    }
  }

  private scheduleApplyMultipleSummaryLabel(dates?: Date[] | null): void {
    if (this.selectionMode !== 'multiple') {
      return;
    }

    if (this.summaryLabelFallbackTimer !== null) {
      clearTimeout(this.summaryLabelFallbackTimer);
    }

    this.summaryLabelFallbackTimer = setTimeout(() => {
      this.summaryLabelFallbackTimer = null;
      this.applyMultipleSummaryLabel(dates);
    }, 0);
  }

  private applyMultipleSummaryLabel(dates?: Date[] | null): void {
    if (this.selectionMode !== 'multiple' || !this.calendar?.isMultipleSelection?.()) {
      return;
    }

    const input = this.calendar.inputfieldViewChild?.nativeElement as HTMLInputElement | undefined;
    if (!input) {
      return;
    }

    const selectedDates = dates ?? (Array.isArray(this._value) ? this._value : []);
    const count = selectedDates.filter(Boolean).length;

    if (count === 0) {
      return;
    }

    if (count > this.effectiveMaxSelectedLabels) {
      input.value = this.selectedItemsLabel.replace(this.replaceRegExp, String(count));
    }
  }

  public ngOnDestroy() {
    if (this.summaryLabelFallbackTimer !== null) {
      clearTimeout(this.summaryLabelFallbackTimer);
      this.summaryLabelFallbackTimer = null;
    }
    this.restoreUpdateInputfieldPatch();
    clearInterval(this.timer);
  }
}
